import { useState, useEffect } from 'react'
import { Download, Filter, X, Loader2 } from 'lucide-react'
import leadsService from '../../services/leadsService'
import AlertModal from '../../components/popups/confirmationModal/AlertModal'

const Leads = () => {
    const [leads, setLeads] = useState([])
    const [isLoading, setIsLoading] = useState(false)
    const [error, setError] = useState(null)

    // Filter state
    const [filters, setFilters] = useState({
        timezone: 'IST',
        from_date: '',
        to_date: '',
        field_name: '',
        field_value: ''
    })

    const [showFilters, setShowFilters] = useState(false)
    const [alertModal, setAlertModal] = useState({
        isOpen: false,
        title: '',
        message: '',
        type: 'info'
    })

    // Load leads on mount and when filters change
    useEffect(() => {
        loadLeads()
    }, [])

    const loadLeads = async () => {
        try {
            setIsLoading(true)
            setError(null)

            const data = await leadsService.getLeads(filters)
            setLeads(Array.isArray(data) ? data : [])
        } catch (err) {
            console.error('Error loading leads:', err)
            setError(err.message || 'Failed to load leads')
            setAlertModal({
                isOpen: true,
                title: 'Error',
                message: err.message || 'Failed to load leads. Please try again.',
                type: 'danger'
            })
        } finally {
            setIsLoading(false)
        }
    }

    const handleFilterChange = (key, value) => {
        setFilters(prev => ({
            ...prev,
            [key]: value
        }))
    }

    const handleApplyFilters = () => {
        loadLeads()
    }

    const handleResetFilters = () => {
        setFilters({
            timezone: 'IST',
            from_date: '',
            to_date: '',
            field_name: '',
            field_value: ''
        })
    }

    const formatDate = (dateString) => {
        if (!dateString) return '-'
        try {
            const date = new Date(dateString)
            return date.toLocaleString('en-IN', {
                year: 'numeric',
                month: 'short',
                day: 'numeric',
                hour: '2-digit',
                minute: '2-digit'
            })
        } catch {
            return dateString
        }
    }

    const exportLeads = () => {
        if (leads.length === 0) {
            setAlertModal({
                isOpen: true,
                title: 'No Data',
                message: 'No leads to export.',
                type: 'warning'
            })
            return
        }

        // Convert leads to CSV
        const headers = Object.keys(leads[0] || {})
        const csvContent = [
            headers.join(','),
            ...leads.map(lead =>
                headers.map(header => {
                    const value = lead[header]
                    return typeof value === 'string' && value.includes(',')
                        ? `"${value}"`
                        : value || ''
                }).join(',')
            )
        ].join('\n')

        const blob = new Blob([csvContent], { type: 'text/csv' })
        const url = URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.href = url
        a.download = `leads_${new Date().toISOString().split('T')[0]}.csv`
        document.body.appendChild(a)
        a.click()
        document.body.removeChild(a)
        URL.revokeObjectURL(url)
    }

    // Get timezone options
    const timezones = [
        { value: 'IST', label: 'IST (Indian Standard Time)' },
        { value: 'UTC', label: 'UTC' },
        { value: 'EST', label: 'EST (Eastern Standard Time)' },
        { value: 'PST', label: 'PST (Pacific Standard Time)' },
        { value: 'GMT', label: 'GMT (Greenwich Mean Time)' }
    ]

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-3xl font-bold text-white mb-2">Leads</h1>
                    <p className="text-secondary-400">
                        View and manage leads collected from your chatbot
                    </p>
                </div>
                <div className="flex items-center space-x-3">
                    <button
                        onClick={() => setShowFilters(!showFilters)}
                        className="flex items-center space-x-2 px-4 py-2 bg-secondary-800 hover:bg-secondary-700 text-white rounded-lg transition-colors"
                    >
                        <Filter className="w-4 h-4" />
                        <span>Filters</span>
                    </button>
                    <button
                        onClick={exportLeads}
                        disabled={leads.length === 0}
                        className="flex items-center space-x-2 px-4 py-2 bg-primary-500 hover:bg-primary-600 disabled:bg-primary-500/50 disabled:cursor-not-allowed text-white rounded-lg transition-colors"
                    >
                        <Download className="w-4 h-4" />
                        <span>Export</span>
                    </button>
                </div>
            </div>

            {/* Filters Panel */}
            {showFilters && (
                <div className="bg-secondary-800 rounded-lg p-6 border border-secondary-700">
                    <div className="flex items-center justify-between mb-4">
                        <h3 className="text-lg font-semibold text-white">Filter Leads</h3>
                        <button
                            onClick={() => setShowFilters(false)}
                            className="text-secondary-400 hover:text-white"
                        >
                            <X className="w-5 h-5" />
                        </button>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {/* Timezone */}
                        <div>
                            <label className="block text-sm font-medium text-white mb-2">
                                Timezone
                            </label>
                            <select
                                value={filters.timezone}
                                onChange={(e) => handleFilterChange('timezone', e.target.value)}
                                className="w-full px-3 py-2 bg-secondary-900 border border-secondary-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
                            >
                                {timezones.map(tz => (
                                    <option key={tz.value} value={tz.value}>{tz.label}</option>
                                ))}
                            </select>
                        </div>

                        {/* From Date */}
                        <div>
                            <label className="block text-sm font-medium text-white mb-2">
                                From Date (YYYY-M-D)
                            </label>
                            <input
                                type="text"
                                value={filters.from_date}
                                onChange={(e) => handleFilterChange('from_date', e.target.value)}
                                placeholder="2025-1-15"
                                className="w-full px-3 py-2 bg-secondary-900 border border-secondary-600 rounded-lg text-white placeholder-secondary-400 focus:outline-none focus:ring-2 focus:ring-primary-500"
                            />
                        </div>

                        {/* To Date */}
                        <div>
                            <label className="block text-sm font-medium text-white mb-2">
                                To Date (YYYY-M-D)
                            </label>
                            <input
                                type="text"
                                value={filters.to_date}
                                onChange={(e) => handleFilterChange('to_date', e.target.value)}
                                placeholder="2025-1-20"
                                className="w-full px-3 py-2 bg-secondary-900 border border-secondary-600 rounded-lg text-white placeholder-secondary-400 focus:outline-none focus:ring-2 focus:ring-primary-500"
                            />
                        </div>

                        {/* Field Name */}
                        <div>
                            <label className="block text-sm font-medium text-white mb-2">
                                Field Name
                            </label>
                            <input
                                type="text"
                                value={filters.field_name}
                                onChange={(e) => handleFilterChange('field_name', e.target.value)}
                                placeholder="e.g., email_address"
                                className="w-full px-3 py-2 bg-secondary-900 border border-secondary-600 rounded-lg text-white placeholder-secondary-400 focus:outline-none focus:ring-2 focus:ring-primary-500"
                            />
                        </div>

                        {/* Field Value */}
                        <div>
                            <label className="block text-sm font-medium text-white mb-2">
                                Field Value
                            </label>
                            <input
                                type="text"
                                value={filters.field_value}
                                onChange={(e) => handleFilterChange('field_value', e.target.value)}
                                placeholder="e.g., user@example.com"
                                className="w-full px-3 py-2 bg-secondary-900 border border-secondary-600 rounded-lg text-white placeholder-secondary-400 focus:outline-none focus:ring-2 focus:ring-primary-500"
                            />
                        </div>
                    </div>

                    {/* Filter Actions */}
                    <div className="flex items-center justify-end space-x-3 mt-6">
                        <button
                            onClick={handleResetFilters}
                            className="px-4 py-2 text-sm font-medium text-secondary-300 hover:text-white transition-colors"
                        >
                            Reset
                        </button>
                        <button
                            onClick={handleApplyFilters}
                            className="px-6 py-2 bg-primary-500 hover:bg-primary-600 text-white text-sm font-medium rounded-lg transition-colors"
                        >
                            Apply Filters
                        </button>
                    </div>
                </div>
            )}

            {/* Error Message */}
            {error && (
                <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-4">
                    <p className="text-red-400">{error}</p>
                </div>
            )}

            {/* Leads Table */}
            <div className="bg-secondary-800 rounded-lg border border-secondary-700 overflow-hidden">
                {isLoading ? (
                    <div className="flex items-center justify-center py-12">
                        <div className="flex items-center space-x-3 text-secondary-400">
                            <Loader2 className="w-6 h-6 animate-spin" />
                            <span>Loading leads...</span>
                        </div>
                    </div>
                ) : leads.length === 0 ? (
                    <div className="text-center py-12">
                        <p className="text-secondary-400">No leads found</p>
                        <p className="text-secondary-500 text-sm mt-2">
                            Try adjusting your filters or check back later
                        </p>
                    </div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full">
                            <thead className="bg-secondary-900 border-b border-secondary-700">
                                <tr>
                                    {Object.keys(leads[0] || {}).map((key) => (
                                        <th
                                            key={key}
                                            className="px-6 py-3 text-left text-xs font-medium text-secondary-400 uppercase tracking-wider"
                                        >
                                            {key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                                        </th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-secondary-700">
                                {leads.map((lead, index) => (
                                    <tr key={index} className="hover:bg-secondary-700/50 transition-colors">
                                        {Object.keys(leads[0] || {}).map((key) => (
                                            <td
                                                key={key}
                                                className="px-6 py-4 whitespace-nowrap text-sm text-white"
                                            >
                                                {key.includes('date') || key.includes('time') || key.includes('created') || key.includes('updated')
                                                    ? formatDate(lead[key])
                                                    : String(lead[key] || '-')}
                                            </td>
                                        ))}
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            {/* Stats */}
            {leads.length > 0 && (
                <div className="bg-secondary-800 rounded-lg p-4 border border-secondary-700">
                    <p className="text-secondary-400 text-sm">
                        Showing <span className="text-white font-medium">{leads.length}</span> lead{leads.length !== 1 ? 's' : ''}
                    </p>
                </div>
            )}

            {/* Alert Modal */}
            <AlertModal
                isOpen={alertModal.isOpen}
                onClose={() => setAlertModal({ ...alertModal, isOpen: false })}
                title={alertModal.title}
                message={alertModal.message}
                type={alertModal.type}
            />
        </div>
    )
}

export default Leads

